import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import axios from '../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faPowerOff, faToggleOn, faToggleOff, faPlay, faStop, faTasks, faFilter, faChevronLeft, faChevronRight, faSearch } from '@fortawesome/free-solid-svg-icons';

const SurveySelection = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [showControllerModal, setShowControllerModal] = useState(false);
  const showControllerModalDismiss = useModalDismiss(() => setShowControllerModal(false), { backdropClose: false, enabled: showControllerModal });
  const [currentSurveyId, setCurrentSurveyId] = useState(null);
  const [controllers, setControllers] = useState([]);
  const [selectedControllerIds, setSelectedControllerIds] = useState([]);
  const [assignedControllers, setAssignedControllers] = useState([]);
  const [loadingControllers, setLoadingControllers] = useState(false);
  const [loadingAssignedControllers, setLoadingAssignedControllers] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const showSuccessModalDismiss = useModalDismiss(() => setShowSuccessModal(false), { backdropClose: false, enabled: showSuccessModal });
  const [successMessage, setSuccessMessage] = useState('');
  const [controllerSearch, setControllerSearch] = useState('');
  const [showControllerDropdown, setShowControllerDropdown] = useState(false);
  const [controllerDropdownRef, setControllerDropdownRef] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSurveys, setTotalSurveys] = useState(0);
  const surveysPerPage = 10;

  useEffect(() => {
    fetchControllers();
  }, []);

  useEffect(() => {
    fetchSurveys();
  }, [currentPage, activeSearchTerm, statusFilter]);

  const fetchControllers = async () => {
    try {
      setLoadingControllers(true);
      // Request all controllers without pagination
      const response = await axios.get('/controllers', {
        params: {
          per_page: 1000 // Get all controllers
        }
      });
      
      // Debug: log the response to see its structure
      console.log('Controllers API response:', response.data);
      
      // The API returns { data: [...], current_page: ..., ... }
      let controllersList = [];
      if (response.data) {
        // Check if response.data.data exists and is an array
        if (response.data.data && Array.isArray(response.data.data)) {
          controllersList = response.data.data;
        } 
        // Fallback: if response.data is directly an array
        else if (Array.isArray(response.data)) {
          controllersList = response.data;
        }
      }
      
      console.log('Extracted controllers list:', controllersList);
      console.log('Controllers count:', controllersList.length);
      
      // Always ensure it's an array
      const finalList = Array.isArray(controllersList) ? controllersList : [];
      setControllers(finalList);
      
      if (finalList.length === 0) {
        console.warn('No controllers found in response');
      }
    } catch (error) {
      console.error('Error fetching controllers:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setControllers([]); // Ensure it's always an array
    } finally {
      setLoadingControllers(false);
    }
  };

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: surveysPerPage
      });
      
      if (activeSearchTerm) {
        params.append('search', activeSearchTerm);
      }
      
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      
      const response = await axios.get(`/control/surveys?${params.toString()}`);
      
      // Handle both paginated and non-paginated responses
      if (response.data.data) {
        setSurveys(response.data.data);
        setTotalPages(response.data.last_page || 1);
        setTotalSurveys(response.data.total || response.data.data.length);
      } else {
        setSurveys(response.data);
        setTotalPages(1);
        setTotalSurveys(response.data.length);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
      setSuccessMessage('Erreur lors du chargement des enquêtes');
      setShowSuccessModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearch = () => {
    setActiveSearchTerm(searchTerm);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleSearchInputChange = (term) => {
    setSearchTerm(term);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleEnableSurvey = async (surveyId) => {
    try {
      setProcessing(prev => ({ ...prev, [surveyId]: true }));
      await axios.post('/control/surveys/enable', {
        survey_ids: [surveyId]
      });
      setSuccessMessage('Enquête activée pour le contrôle');
      setShowSuccessModal(true);
      fetchSurveys();
    } catch (error) {
      console.error('Error enabling survey:', error);
      setSuccessMessage('Erreur lors de l\'activation');
      setShowSuccessModal(true);
    } finally {
      setProcessing(prev => ({ ...prev, [surveyId]: false }));
    }
  };

  const handleDisableSurvey = async (surveyId) => {
    try {
      setProcessing(prev => ({ ...prev, [surveyId]: true }));
      await axios.post('/control/surveys/disable', {
        survey_ids: [surveyId]
      });
      setSuccessMessage('Enquête désactivée pour le contrôle');
      setShowSuccessModal(true);
      fetchSurveys();
    } catch (error) {
      console.error('Error disabling survey:', error);
      setSuccessMessage('Erreur lors de la désactivation');
      setShowSuccessModal(true);
    } finally {
      setProcessing(prev => ({ ...prev, [surveyId]: false }));
    }
  };

  const handleSwitchToFullControl = async (surveyId) => {
    try {
      setProcessing(prev => ({ ...prev, [surveyId]: true }));
      await axios.post(`/control/surveys/${surveyId}/enable-full-control`);
      setSuccessMessage('Mode Contrôle Complet activé');
      setShowSuccessModal(true);
      fetchSurveys();
    } catch (error) {
      console.error('Error switching to full control:', error);
      setSuccessMessage('Erreur lors du changement de mode');
      setShowSuccessModal(true);
    } finally {
      setProcessing(prev => ({ ...prev, [surveyId]: false }));
    }
  };

  const handleSwitchToQualityBatches = async (surveyId) => {
    try {
      setProcessing(prev => ({ ...prev, [surveyId]: true }));
      await axios.post(`/control/surveys/${surveyId}/enable-quality-batches`);
      setSuccessMessage('Mode Lots de Qualité activé');
      setShowSuccessModal(true);
      fetchSurveys();
    } catch (error) {
      console.error('Error switching to quality batches:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors du changement de mode';
      setSuccessMessage(errorMessage);
      setShowSuccessModal(true);
    } finally {
      setProcessing(prev => ({ ...prev, [surveyId]: false }));
    }
  };

  const fetchAssignedControllers = async (surveyId) => {
    try {
      setLoadingAssignedControllers(true);
      // Add cache-busting timestamp to prevent stale cached responses
      const response = await axios.get(`/control/surveys/${surveyId}/assigned-controllers?_t=${Date.now()}`);
      if (response.data.success) {
        const assigned = response.data.data || [];
        setAssignedControllers(assigned);
        // Pre-select already assigned controllers so they remain selected
        const assignedIds = assigned.map(c => c.id);
        setSelectedControllerIds(assignedIds);
        return assigned;
      } else {
        setAssignedControllers([]);
        return [];
      }
    } catch (error) {
      console.error('Error fetching assigned controllers:', error);
      setAssignedControllers([]);
      return [];
    } finally {
      setLoadingAssignedControllers(false);
    }
  };

  const handleGenerateAssignments = async (surveyId) => {
    setCurrentSurveyId(surveyId);
    setSelectedControllerIds([]);
    setControllerSearch('');
    setShowControllerDropdown(false);
    setAssignedControllers([]);
    setShowControllerModal(true);
    // Fetch assigned controllers when modal opens and pre-select them
    const assigned = await fetchAssignedControllers(surveyId);
    if (assigned && assigned.length > 0) {
      const assignedIds = assigned.map(c => c.id);
      setSelectedControllerIds(assignedIds);
    }
  };

  const handleControllerSelect = (controller) => {
    // Prevent selection during processing
    if (processing[currentSurveyId]) {
      return;
    }
    if (!selectedControllerIds.includes(controller.id)) {
      setSelectedControllerIds(prev => [...prev, controller.id]);
    }
    setControllerSearch('');
    setShowControllerDropdown(false);
  };

  const handleRemoveController = (controllerId) => {
    // Prevent removal during processing
    if (processing[currentSurveyId]) {
      return;
    }
    setSelectedControllerIds(prev => prev.filter(id => id !== controllerId));
  };

  const getFilteredControllers = () => {
    // Show active and inactive controllers; only suspended ones are excluded.
    let filtered = controllers.filter(c => c.status !== 'suspended');
    
    if (controllerSearch) {
      filtered = filtered.filter(controller => 
        controller.name.toLowerCase().includes(controllerSearch.toLowerCase()) ||
        (controller.ref && controller.ref.toLowerCase().includes(controllerSearch.toLowerCase())) ||
        (controller.email && controller.email.toLowerCase().includes(controllerSearch.toLowerCase()))
      );
    }
    
    // Exclude already selected controllers
    filtered = filtered.filter(controller => !selectedControllerIds.includes(controller.id));

    return filtered; // Show all matching controllers (no limit)
  };

  // Click outside handler for controller dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (controllerDropdownRef && !controllerDropdownRef.contains(event.target)) {
        setShowControllerDropdown(false);
      }
    };

    if (showControllerModal) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [controllerDropdownRef, showControllerModal]);

  const handleConfirmGenerateAssignments = async () => {
    if (selectedControllerIds.length === 0) {
      setSuccessMessage('Veuillez sélectionner au moins un contrôleur');
      setShowSuccessModal(true);
      return;
    }

    try {
      setProcessing(prev => ({ ...prev, [currentSurveyId]: true }));
      // Set timeout to 10 minutes (600000ms) for this long-running operation
      const response = await axios.post(`/control/surveys/${currentSurveyId}/generate-assignments`, {
        controller_ids: selectedControllerIds
      }, {
        timeout: 600000 // 10 minutes timeout
      });
      
      // Determine message based on what happened
      const assignedIds = assignedControllers.map(c => c.id);
      const newControllers = selectedControllerIds.filter(id => !assignedIds.includes(id));
      const removedControllers = assignedIds.filter(id => !selectedControllerIds.includes(id));
      
      let message = '';
      if (newControllers.length > 0 && removedControllers.length > 0) {
        message = `Contrôleurs mis à jour: ${newControllers.length} ajouté(s), ${removedControllers.length} retiré(s). ${response.data.count || 0} nouvelles affectations créées.`;
      } else if (newControllers.length > 0) {
        message = `${newControllers.length} contrôleur(s) ajouté(s) aux lots existants. ${response.data.count || 0} nouvelles affectations créées.`;
      } else if (removedControllers.length > 0) {
        message = `${removedControllers.length} contrôleur(s) retiré(s) des lots. ${response.data.count || 0} nouvelles affectations créées.`;
      } else {
        message = `${response.data.count || 0} nouvelles affectations créées.`;
      }
      
      setSuccessMessage(message);
      setShowSuccessModal(true);
      fetchSurveys();
      // Refresh assigned controllers list after generating assignments
      if (currentSurveyId) {
        await fetchAssignedControllers(currentSurveyId);
      }
      // Keep modal open to show updated assigned controllers
      // setShowControllerModal(false);
    } catch (error) {
      console.error('Error generating assignments:', error);
      
      // Always show informational message instead of error
      // The process may take longer but will complete in the background
      setSuccessMessage('Cette opération prend plus de temps que prévu. Le processus est en cours de génération en arrière-plan. Veuillez patienter et rafraîchir la page dans quelques instants pour voir les résultats.');
      setShowSuccessModal(true);
    } finally {
      setProcessing(prev => ({ ...prev, [currentSurveyId]: false }));
      // Don't reset currentSurveyId here so we can keep showing assigned controllers
      // setCurrentSurveyId(null);
    }
  };

  const handleToggleController = (controllerId) => {
    setSelectedControllerIds(prev => 
      prev.includes(controllerId) 
        ? prev.filter(id => id !== controllerId)
        : [...prev, controllerId]
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      {/* Header */}
      <header className="relative z-10 p-6 animate-fade-in">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            Sélection des Enquêtes pour Contrôle
          </h1>
          <p className="text-gray-600">
            Activez les enquêtes que vous souhaitez soumettre au contrôle qualité
          </p>
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-6 animate-slide-up">
        <div className="max-w-7xl mx-auto">
          {/* Search and Filter */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <input
                  type="text"
                  placeholder="Rechercher par titre ou ID LimeSurvey..."
                  value={searchTerm}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none flex-1 min-w-0"
                />
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 h-[42px] bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center flex-shrink-0"
                  title="Rechercher"
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="px-4 py-2 h-[42px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none w-full sm:w-auto flex-shrink-0"
              >
                <option value="">Tous les statuts</option>
                <option value="active">Activé</option>
                <option value="inactive">Désactivé</option>
                <option value="full_control">Contrôle Complet</option>
              </select>
            </div>
          </div>

          {/* Surveys Table */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Chargement des enquêtes...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Enquête
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID LimeSurvey
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Soumissions Complètes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        % Contrôle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Progression Contrôle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut Contrôle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mode de Contrôle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {surveys.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                          Aucune enquête disponible
                        </td>
                      </tr>
                    ) : (
                      surveys.map((survey) => {
                        const controlPercentage = survey.control_percentage;
                        const controlTarget = survey.control_target;
                        const controlled = survey.done_control_assignments_count || 0;
                        const humanCount = survey.done_human_count || 0;
                        const aiCount = survey.done_ai_only_count || 0;
                        const remaining = survey.control_remaining;
                        const progressPercent = controlTarget && controlTarget > 0
                          ? Math.min(100, Math.round((controlled / controlTarget) * 100))
                          : null;
                        const humanWidth = controlTarget && controlTarget > 0
                          ? Math.min(100, (humanCount / controlTarget) * 100)
                          : 0;
                        const aiWidth = controlTarget && controlTarget > 0
                          ? Math.min(100 - humanWidth, (aiCount / controlTarget) * 100)
                          : 0;
                        const totalDone = humanCount + aiCount;
                        const humanPct = totalDone > 0 ? Math.round((humanCount / totalDone) * 100) : 0;
                        const aiPct = totalDone > 0 ? Math.round((aiCount / totalDone) * 100) : 0;
                        const isComplete = remaining !== null && remaining <= 0;

                        return (
                        <tr key={survey.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{survey.title}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {survey.limesurvey_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {survey.completed_submissions_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700">
                            {controlPercentage != null ? `${controlPercentage}%` : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {controlTarget != null ? (
                              <div className="min-w-[160px]">
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`font-semibold ${isComplete ? 'text-green-600' : 'text-blue-600'}`}>
                                    {controlled}/{controlTarget}
                                  </span>
                                  {remaining > 0 && (
                                    <span className="text-xs text-orange-600 font-medium ml-2">
                                      reste {remaining}
                                    </span>
                                  )}
                                  {isComplete && (
                                    <span className="text-xs text-green-600 font-medium ml-2">
                                      Atteint
                                    </span>
                                  )}
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2 flex overflow-hidden">
                                  <div
                                    className={`h-2 transition-all ${isComplete && aiCount === 0 ? 'bg-green-500' : 'bg-blue-500'}`}
                                    style={{ width: `${humanWidth}%` }}
                                    title={`Humain: ${humanCount} (${humanPct}%)`}
                                  ></div>
                                  <div
                                    className="h-2 bg-indigo-500 transition-all"
                                    style={{ width: `${aiWidth}%` }}
                                    title={`IA: ${aiCount} (${aiPct}%)`}
                                  ></div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {survey.control_enabled ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Activé
                              </span>
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                Désactivé
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {survey.control_enabled && survey.control_mode ? (
                              survey.control_mode === 'full_control' ? (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                  Contrôle Complet
                                </span>
                              ) : (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                                  Lots de Qualité
                                </span>
                              )
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-500">
                                -
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              {survey.control_enabled ? (
                                <>
                                  <button
                                    onClick={() => handleDisableSurvey(survey.id)}
                                    disabled={processing[survey.id]}
                                    className="relative group px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                  >
                                    <FontAwesomeIcon icon={faPowerOff} />
                                    <span className="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded whitespace-nowrap z-50 pointer-events-none">
                                      Désactiver
                                      <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></span>
                                    </span>
                                  </button>
                                  {survey.control_mode !== 'full_control' && (
                                    <button
                                      onClick={() => handleSwitchToFullControl(survey.id)}
                                      disabled={processing[survey.id]}
                                      className="relative group px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                      title="Passer en mode Contrôle Complet"
                                    >
                                      <FontAwesomeIcon icon={faToggleOn} />
                                      <span className="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded whitespace-nowrap z-50 pointer-events-none">
                                        Contrôle Complet
                                        <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></span>
                                      </span>
                                    </button>
                                  )}
                                  {survey.control_mode !== 'quality_batches' && survey.control_mode !== 'full_control' && (
                                    <button
                                      onClick={() => handleSwitchToQualityBatches(survey.id)}
                                      disabled={processing[survey.id]}
                                      className="relative group px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                      title="Passer en mode Lots de Qualité"
                                    >
                                      <FontAwesomeIcon icon={faToggleOn} />
                                      <span className="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded whitespace-nowrap z-50 pointer-events-none">
                                        Lots de Qualité
                                        <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></span>
                                      </span>
                                    </button>
                                  )}
                                </>
                              ) : (
                                <button
                                  onClick={() => handleEnableSurvey(survey.id)}
                                  disabled={processing[survey.id]}
                                  className="relative group px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                >
                                  <FontAwesomeIcon icon={faPlay} />
                                  <span className="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded whitespace-nowrap z-50 pointer-events-none">
                                    Activer
                                    <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></span>
                                  </span>
                                </button>
                              )}
                              <button
                                onClick={() => handleGenerateAssignments(survey.id)}
                                disabled={processing[survey.id] || !survey.control_enabled}
                                className="relative group px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-black disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                              >
                                <FontAwesomeIcon icon={faTasks} />
                                <span className="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded whitespace-nowrap z-50 pointer-events-none">
                                  Générer Affectations
                                  <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></span>
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
              <div className="flex items-center text-sm text-gray-700">
                  <span>
                    Affichage de {((currentPage - 1) * surveysPerPage) + 1} à {Math.min(currentPage * surveysPerPage, totalSurveys)} sur {totalSurveys} enquête{totalSurveys > 1 ? 's' : ''}
                  </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? 'bg-red-600 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controller Selection Modal */}
      {showControllerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">
                Sélectionner les Contrôleurs
              </h2>
              <button
                onClick={() => {
                  setShowControllerModal(false);
                  setSelectedControllerIds([]);
                  setCurrentSurveyId(null);
                  setControllerSearch('');
                  setShowControllerDropdown(false);
                  setAssignedControllers([]);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-gray-600 mb-4">
                Recherchez et sélectionnez les contrôleurs pour assigner les lots de qualité générés. 
                Les lots seront assignés aux contrôleurs sélectionnés qui n'ont pas déjà un lot actif.
              </p>
              
              {/* Already Assigned Controllers Section */}
              {loadingAssignedControllers ? (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <p className="text-sm text-blue-700">Chargement des contrôleurs déjà affectés...</p>
                  </div>
                </div>
              ) : assignedControllers.length > 0 ? (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-semibold text-blue-900 mb-2">
                    Contrôleurs déjà affectés à cette enquête ({assignedControllers.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {assignedControllers.map((controller) => (
                      <div
                        key={controller.id}
                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        <span>{controller.name}</span>
                        {controller.ref && (
                          <span className="text-xs text-blue-600">({controller.ref})</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              
              {loadingControllers ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Chargement des contrôleurs...</p>
                </div>
              ) : (
                <>
                  {/* Controller Search Input */}
                  <div className="mb-4 relative" ref={setControllerDropdownRef}>
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faSearch} className="text-gray-400 absolute left-3 z-10" />
                      <input
                        type="text"
                        value={controllerSearch}
                        onChange={(e) => {
                          setControllerSearch(e.target.value);
                          setShowControllerDropdown(true);
                        }}
                        onFocus={() => setShowControllerDropdown(true)}
                        disabled={processing[currentSurveyId]}
                        className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                          processing[currentSurveyId] ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''
                        }`}
                        placeholder="Rechercher un contrôleur par nom, ref ou email..."
                      />
                    </div>
                    
                    {/* Controller Dropdown */}
                    {showControllerDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {getFilteredControllers().length > 0 ? (
                          getFilteredControllers().map((controller) => {
                            const isAssigned = assignedControllers.some(ac => ac.id === controller.id);
                            return (
                            <div
                              key={controller.id}
                                className={`px-3 py-2 border-b border-gray-100 last:border-b-0 ${
                                  processing[currentSurveyId] 
                                    ? 'bg-gray-50 cursor-not-allowed opacity-50' 
                                    : isAssigned 
                                      ? 'bg-blue-50 hover:bg-gray-100 cursor-pointer' 
                                      : 'hover:bg-gray-100 cursor-pointer'
                                }`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                if (!processing[currentSurveyId]) {
                                  handleControllerSelect(controller);
                                }
                              }}
                            >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">
                                      {controller.name}
                                      {isAssigned && (
                                        <span className="ml-2 text-xs text-blue-600 font-semibold">(Déjà affecté)</span>
                                      )}
                                    </div>
                              <div className="text-sm text-gray-500">
                                {controller.ref && `Ref: ${controller.ref}`}
                                {controller.email && ` • ${controller.email}`}
                              </div>
                            </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="px-3 py-2 text-gray-500">
                            {controllerSearch ? 'Aucun contrôleur trouvé' : 'Commencez à taper pour rechercher'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Selected Controllers */}
                  {selectedControllerIds.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Contrôleurs sélectionnés ({selectedControllerIds.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {controllers
                          .filter(c => selectedControllerIds.includes(c.id))
                          .map((controller) => {
                            const isAssigned = assignedControllers.some(ac => ac.id === controller.id);
                            return (
                            <div
                              key={controller.id}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                                  isAssigned 
                                    ? 'bg-orange-100 text-orange-800 border border-orange-300' 
                                    : 'bg-red-100 text-red-800'
                                }`}
                            >
                              <span>{controller.name}</span>
                                {isAssigned && (
                                  <span className="text-xs italic">(déjà affecté)</span>
                                )}
                              <button
                                onClick={() => handleRemoveController(controller.id)}
                                disabled={processing[currentSurveyId]}
                                className={`transition-colors ${
                                  processing[currentSurveyId]
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : isAssigned 
                                      ? 'text-orange-600 hover:text-orange-800' 
                                      : 'text-red-600 hover:text-red-800'
                                }`}
                              >
                                <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                              </button>
                            </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {!Array.isArray(controllers) || controllers.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Aucun contrôleur disponible</p>
                  ) : selectedControllerIds.length === 0 && (
                    <p className="text-gray-500 text-center py-8">
                      Aucun contrôleur sélectionné. Utilisez la barre de recherche ci-dessus pour ajouter des contrôleurs.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowControllerModal(false);
                  setSelectedControllerIds([]);
                  setCurrentSurveyId(null);
                  setControllerSearch('');
                  setShowControllerDropdown(false);
                  setAssignedControllers([]);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmGenerateAssignments}
                disabled={selectedControllerIds.length === 0 || processing[currentSurveyId]}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {processing[currentSurveyId] ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Génération en cours...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Générer Affectations</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className={`rounded-full p-3 ${
                  successMessage.includes('Erreur') || successMessage.includes('temps')
                    ? 'bg-yellow-100'
                    : 'bg-green-100'
                }`}>
                  <FontAwesomeIcon 
                    icon={faCheck} 
                    className={`text-2xl ${
                      successMessage.includes('Erreur') || successMessage.includes('temps')
                        ? 'text-yellow-600'
                        : 'text-green-600'
                    }`} 
                  />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
                {successMessage.includes('Erreur') ? 'Erreur' : successMessage.includes('temps') ? 'Information' : 'Succès'}
              </h3>
              <p className="text-gray-600 text-center mb-6 whitespace-pre-line">
                {successMessage}
              </p>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setSuccessMessage('');
                }}
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                  successMessage.includes('Erreur')
                    ? 'bg-gray-600 text-white hover:bg-gray-700'
                    : successMessage.includes('temps')
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveySelection;

